import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { RiGraduationCapLine, RiPencilLine, RiStarFill } from "@remixicon/react";
import { getAllTeachers, getPayCycleVersions, getTutorsTeachingHoursCycle } from "../../services/dataService";
import { cyclePeriod } from "../../utils/payCycle";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { showingOfLabel } from "../../utils/kitPresentation";
import {
  PAGE_SIZES,
  TUTOR_FILTERS,
  visibleTutors,
  pageSlice,
  overviewTiles,
  verifyChip,
  subjectChips,
  genderText,
  ratingText,
  hoursText,
  owedText,
  earnedText,
  tutorEmptyState,
} from "../../utils/tutorPresentation";
import { srOnly, tokens } from "../../utils/figmaTokens";
import { SectionCard, TableCard } from "../../components/kit/SectionCard";
import StatStrip from "../../components/kit/StatStrip";
import { StateAction } from "../../components/kit/States";
import { EyeButton, PersonCell, RowAction, SkeletonRows, SortHead, TableMessageRow } from "../../components/kit/TableParts";
import Pager from "../../components/kit/Pager";
import SearchField from "../../components/kit/SearchField";
import ToolbarMenu from "../../components/kit/ToolbarMenu";
import EditTutorDialog from "./EditTutorDialog";

// The Tutors list, drawn from Figma's Teachers frame 10960:51268 (047 D35–D42): the overview
// card, then the table card with its search, Status menu, sortable Tutor and Owed heads and
// client-side pages. The page still reads every tutor and every booking on each visit (D37).
// A row, its eye and Ctrl+K open the tutor page (047 step 4); the pencil opens the Edit dialog
// the page shares.

const COLUMNS = 10;

const Tutors = () => {
  const { user } = useAuth();
  const userRole = user?.role || "";
  const canEdit = hasPermission(userRole, permissions.EDIT_TUTORS);
  const navigate = useNavigate();
  // Single useSearchParams call for the page (spec 024 ruling: T9 reads its
  // own `open` param alongside these, not via a second hook call).
  const [searchParams, setSearchParams] = useSearchParams();
  const [tutors, setTutors] = useState([]);
  // null until config/app is read, which payCycle.js reads as the shipped default.
  const [cycleVersions, setCycleVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(""); // the error block's line; "" when the list loaded

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [verifyFilter, setVerifyFilter] = useState("all"); // all | pending | approved
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const fetchTutors = useCallback(async () => {
    try {
      setLoading(true);
      // Read the cycle before the hours, so the labels and the numbers below are
      // built from the same rule rather than one render apart.
      setCycleVersions(await getPayCycleVersions());
      const data = await getAllTeachers();
      // compute cycle hours from finished bookings (26th to 25th)
      const hoursData = await getTutorsTeachingHoursCycle();
      setTutors(
        data.map((t) => ({
          ...t,
          teachingHours: hoursData.current[t.id] ?? 0,
          lastCycleHours: hoursData.previous[t.id] ?? 0,
          lastCycleLessons: hoursData.previousLessons?.[t.id] ?? [],
        }))
      );
      setError("");
    } catch (e) {
      console.error("Error fetching tutors:", e);
      // Error State 10618:2251: always say whether anything was changed.
      setError("Nothing has been changed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTutors();
  }, [fetchTutors]);

  // Attention-queue tile links (spec 024 D7/T8): ?verify=pending pre-sets the
  // status filter below, ?sort=unpaidEarning pre-sorts by what's owed. Does
  // NOT clear searchParams.
  useEffect(() => {
    const verify = searchParams.get("verify");
    if (verify === "pending" || verify === "approved") {
      setVerifyFilter(verify);
      setPage(0);
    }
    const sort = searchParams.get("sort");
    if (sort) {
      setSortField(sort);
      setSortDirection("desc");
      setPage(0);
    }
  }, [searchParams]);

  const visible = useMemo(
    () => visibleTutors(tutors, { term: searchTerm, filter: verifyFilter, sortField, sortDirection }),
    [tutors, searchTerm, verifyFilter, sortField, sortDirection]
  );
  const slice = pageSlice(visible, page, pageSize);

  // A new search, filter, sort or page size starts again on page 1 (P58).
  const changeSearch = (value) => {
    setSearchTerm(value);
    setPage(0);
  };
  const changeFilter = (value) => {
    setVerifyFilter(value);
    setPage(0);
  };
  const changePageSize = (value) => {
    setPageSize(value);
    setPage(0);
  };
  const toggleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
    // 047 D76: the user's sort replaces the ?sort= preset, so Back or a reload keeps theirs.
    if (searchParams.has("sort")) {
      const next = new URLSearchParams(searchParams);
      next.delete("sort");
      setSearchParams(next, { replace: true });
    }
  };

  // Ctrl+K deep link (spec 024 D4/T9) and every older link: ?open=<id> now lands on the tutor
  // page (047 step 4), as the Students list's does.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) navigate(`/tutors/${openId}`, { replace: true });
  }, [searchParams, navigate]);

  const openEdit = (row) => {
    setEditTarget(row);
    setEditOpen(true);
  };

  const failed = Boolean(error) && !loading;
  const now = new Date();
  const tiles = overviewTiles(
    loading || failed
      ? {}
      : { total: tutors.length, current: cyclePeriod(cycleVersions, now), previous: cyclePeriod(cycleVersions, now, -1) }
  );
  const empty = tutorEmptyState({ term: searchTerm, filter: verifyFilter });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {/* Figma draws no page head, so the page's h1 is for screen readers only (P59). */}
      <Typography component="h1" sx={srOnly}>
        Tutors
      </Typography>

      <SectionCard title="Tutors overview" Icon={RiGraduationCapLine} sx={{ bgcolor: tokens.surface.default }}>
        <StatStrip items={tiles} cellGap="8px" />
      </SectionCard>

      <TableCard
        title="Tutors"
        subtitle="Teaching hours follow the pay cycles above."
        toolbar={
          <>
            <SearchField value={searchTerm} onChange={changeSearch} placeholder="Search tutors…" />
            <ToolbarMenu id="tutors-status" label="Status" value={verifyFilter} options={TUTOR_FILTERS} onChange={changeFilter} />
          </>
        }
        footer={
          // While the list loads or after it failed, the label is empty and both buttons are off.
          <Pager
            label={loading || failed ? "" : showingOfLabel(slice.page, slice.rows.length, pageSize, visible.length)}
            page={loading || failed ? 0 : slice.page}
            hasMore={!loading && !failed && slice.hasMore}
            onPage={setPage}
            pageSize={pageSize}
            pageSizes={PAGE_SIZES}
            onPageSize={changePageSize}
          />
        }
      >
        <TableContainer>
          <Table aria-busy={loading}>
            <TableHead>
              <TableRow>
                <SortHead label="Tutor" active={sortField === "name"} direction={sortDirection} onSort={() => toggleSort("name")} />
                <TableCell>Subjects</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Hours this cycle</TableCell>
                <TableCell>Last cycle</TableCell>
                <TableCell>Last cycle (E£)</TableCell>
                <SortHead
                  label="Owed"
                  active={sortField === "unpaidEarning"}
                  direction={sortDirection}
                  onSort={() => toggleSort("unpaidEarning")}
                />
                <TableCell>Status</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={COLUMNS} />
              ) : failed || slice.rows.length === 0 ? (
                <TableMessageRow
                  colSpan={COLUMNS}
                  error={failed ? "Could not load tutors" : ""}
                  errorMessage={error}
                  onRetry={fetchTutors}
                  emptyText={empty.title}
                  emptyMessage={empty.message}
                  emptyActions={
                    empty.clear && (
                      <StateAction outlined onClick={() => (empty.clear === "search" ? changeSearch("") : changeFilter("all"))}>
                        {empty.clear === "search" ? "Clear search" : "Clear filter"}
                      </StateAction>
                    )
                  }
                />
              ) : (
                slice.rows.map((t) => {
                  const name = t.name || "Unknown";
                  const subjects = subjectChips(t.subjects);
                  const status = verifyChip(t);
                  const rating = ratingText(t.rating);
                  return (
                    <TableRow key={t.id} hover onClick={() => navigate(`/tutors/${t.id}`)} sx={{ cursor: "pointer" }}>
                      <TableCell>
                        <PersonCell name={name} colorKey={t.id} sub={t.email || t.phone || "No contact"} />
                      </TableCell>
                      <TableCell>
                        {subjects ? (
                          <Tooltip title={subjects.all} describeChild>
                            <Box sx={{ display: "flex", gap: "4px" }}>
                              <Chip label={subjects.first} color="info" />
                              {subjects.more > 0 && <Chip label={`+${subjects.more}`} color="info" />}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: tokens.text.secondary }}>{genderText(t.gender)}</TableCell>
                      <TableCell sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>{hoursText(t.teachingHours)}</TableCell>
                      <TableCell sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>{hoursText(t.lastCycleHours)}</TableCell>
                      <TableCell sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>{earnedText(t.lastCycleLessons, t)}</TableCell>
                      <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{owedText(t.unpaidEarning)}</TableCell>
                      <TableCell>
                        <Chip label={status.label} color={status.color} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                        {rating === "—" ? (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        ) : (
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                            <RiStarFill size={13} color={tokens.star} aria-hidden />
                            {rating}
                          </Box>
                        )}
                      </TableCell>
                      {/* The row opens the tutor; its buttons must not open it a second time. */}
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                        <EyeButton to={`/tutors/${t.id}`} label={`Open ${t.name || "tutor"}`} />
                        {canEdit && <RowAction Icon={RiPencilLine} label={`Edit ${t.name || "tutor"}`} onClick={() => openEdit(t)} />}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <EditTutorDialog open={editOpen} onClose={() => setEditOpen(false)} tutor={editTarget} actingUser={user} onSaved={fetchTutors} />
    </Box>
  );
};

export default Tutors;
